<template>
  <div class="property-right-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="pageTitle" :subtitle="$t('propertyRightDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2)(3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('propertyRightDetail.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="listPropertyRightRegistrationDetailInfo.propertyRightRegistrationDetails"
        :loading="loading" :error="hasError" :error-message="errorMessage" :density="density" border
        :empty-description="$t('propertyRightDetail.page.empty')" @retry="getList">
        <el-table-column prop="prrdId" :label="$t('propertyRightDetail.table.id')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.prrdId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="securitiesName" :label="$t('propertyRightDetail.table.materialType')" align="center"
          width="140">
          <template slot-scope="scope">
            {{ scope.row.securitiesName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyRightDetail.table.isPayment')" align="center" width="120">
          <template slot-scope="scope">
            {{ scope.row.isTrue === '-1' ? '--' : scope.row.isTrue === 'true' ? $t('common.yes') : $t('common.no') }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('propertyRightDetail.table.image')" align="center" min-width="160">
          <template slot-scope="scope">
            <div v-if="scope.row.securities === '001'">
              <div v-for="(item, index) in scope.row.idCardUrlShow" :key="index" class="image-container">
                <el-image :src="item" :preview-src-list="scope.row.idCardUrlShow" fit="cover"
                  style="width: 50px; height: 50px">
                  <div slot="error" class="image-slot">
                    <img src="/img/noPhoto.jpg" style="width: 50px; height: 50px">
                  </div>
                </el-image>
                <img src="/img/icon-bigimg.png" class="preview-icon">
              </div>
            </div>
            <div v-else-if="scope.row.securities === '002'">
              <div v-for="(item, index) in scope.row.housePurchaseUrlShow" :key="index" class="image-container">
                <el-image :src="item" :preview-src-list="scope.row.housePurchaseUrlShow" fit="cover"
                  style="width: 50px; height: 50px">
                  <div slot="error" class="image-slot">
                    <img src="/img/noPhoto.jpg" style="width: 50px; height: 50px">
                  </div>
                </el-image>
                <img src="/img/icon-bigimg.png" class="preview-icon">
              </div>
            </div>
            <div v-else-if="scope.row.securities === '003'">
              <div v-for="(item, index) in scope.row.repairUrlShow" :key="index" class="image-container">
                <el-image :src="item" :preview-src-list="scope.row.repairUrlShow" fit="cover"
                  style="width: 50px; height: 50px">
                  <div slot="error" class="image-slot">
                    <img src="/img/noPhoto.jpg" style="width: 50px; height: 50px">
                  </div>
                </el-image>
                <img src="/img/icon-bigimg.png" class="preview-icon">
              </div>
            </div>
            <div v-else-if="scope.row.securities === '004'">
              <div v-for="(item, index) in scope.row.deedTaxUrlShow" :key="index" class="image-container">
                <el-image :src="item" :preview-src-list="scope.row.deedTaxUrlShow" fit="cover"
                  style="width: 50px; height: 50px">
                  <div slot="error" class="image-slot">
                    <img src="/img/noPhoto.jpg" style="width: 50px; height: 50px">
                  </div>
                </el-image>
                <img src="/img/icon-bigimg.png" class="preview-icon">
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('propertyRightDetail.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" fixed="right" width="110">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click="_openEditPropertyRightRegistrationDetailModel(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <edit-property-right-registration-detail ref="editPropertyRightRegistrationDetail" @success="handleSuccess" />
  </div>
</template>

<script>
import { listPropertyRightRegistrationDetail } from '@/api/room/listPropertyRightRegistrationDetailApi'
import { getCommunityId } from '@/api/community/communityApi'
import EditPropertyRightRegistrationDetail from '@/components/room/editPropertyRightRegistrationDetail'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'ListPropertyRightRegistrationDetail',
  components: {
    EditPropertyRightRegistrationDetail,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      listPropertyRightRegistrationDetailInfo: {
        propertyRightRegistrationDetails: [],
        conditions: {
          prrId: '',
          securities: '',
          floorNum: '',
          unitNum: '',
          roomNum: '',
          isTrue: '',
          communityId: ''
        }
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  computed: {
    pageTitle () {
      const c = this.listPropertyRightRegistrationDetailInfo.conditions
      const roomCode = [c.floorNum, c.unitNum, c.roomNum].filter(v => v !== '' && v !== null && v !== undefined).join('-')
      return roomCode ? `${roomCode} ${this.$t('propertyRightDetail.title')}` : this.$t('propertyRightDetail.title')
    }
  },
  created() {
    this.listPropertyRightRegistrationDetailInfo.conditions.communityId = getCommunityId()
    this.listPropertyRightRegistrationDetailInfo.conditions.prrId = this.$route.query.prrId
    this.listPropertyRightRegistrationDetailInfo.conditions.floorNum = this.$route.query.floorNum
    this.listPropertyRightRegistrationDetailInfo.conditions.unitNum = this.$route.query.unitNum
    this.listPropertyRightRegistrationDetailInfo.conditions.roomNum = this.$route.query.roomNum
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.listPropertyRightRegistrationDetailInfo.conditions
        }
        const { data, total } = await listPropertyRightRegistrationDetail(params)
        this.listPropertyRightRegistrationDetailInfo.propertyRightRegistrationDetails = data.map(item => {
          if (item.securities === '001' && item.idCardUrl) {
            item.idCardUrl = item.idCardUrl.split(',')
            item.idCardUrlShow = item.idCardUrl
          }
          if (item.securities === '002' && item.housePurchaseUrl) {
            item.housePurchaseUrl = item.housePurchaseUrl.split(',')
            item.housePurchaseUrlShow = item.housePurchaseUrl
          }
          if (item.securities === '003' && item.repairUrl) {
            item.repairUrl = item.repairUrl.split(',')
            item.repairUrlShow = item.repairUrl
          }
          if (item.securities === '004' && item.deedTaxUrl) {
            item.deedTaxUrl = item.deedTaxUrl.split(',')
            item.deedTaxUrlShow = item.deedTaxUrl
          }
          return item
        })
        this.page.total = total
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('propertyRightDetail.fetchError')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(val) {
      this.page.size = val
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    _openEditPropertyRightRegistrationDetailModel(row) {
      this.$refs.editPropertyRightRegistrationDetail.open(row)
    },
    _goBack() {
      this.$router.go(-1)
    },
    handleSuccess() {
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.property-right-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

/* 证件图片 */
.image-container {
  position: relative;
  display: inline-block;
  margin-right: 10px;

  .preview-icon {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 20px;
    height: 20px;
  }
}
</style>
